import { useEffect, useRef, useState } from "react";

/**
 * Keeps a modal open when its save is refused.
 *
 * These forms refuse by design and often: a Stripe item Stripe has never seen,
 * an end date in the past, a new owner who is not in the team. A modal that
 * closed itself before the request landed threw the operator's typing away on
 * every one of them — the refusal arrived as a toast over a page with no form
 * on it, and the only way forward was to reopen and retype.
 *
 * So `onSubmit` is awaited here. The parent closes the modal once it resolves;
 * a rejection is caught and rendered inside the still-open modal, with every
 * field exactly as it was left, so the operator can correct the one thing that
 * was wrong.
 *
 * A caller whose `onSubmit` handles its own errors never rejects, so nothing
 * about its behaviour changes.
 */
export function useModalSubmit(isOpen: boolean) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Reopening is a fresh attempt; last time's refusal would just be noise.
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  async function submit(action: () => void | Promise<void>): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      // A successful save unmounts this modal, so only a failure can still set
      // state here — but the guard keeps a slow reject after a close harmless.
      if (mounted.current) {
        setError((e as Error)?.message || "That change could not be saved.");
      }
    } finally {
      if (mounted.current) {
        setSubmitting(false);
      }
    }
  }

  return { submitting, error, submit };
}
